import React, { useMemo, useState, useRef, useEffect } from 'react';
import Icon from './Icon';
import clsx from 'clsx';

export interface DropdownOption {
  value: string;
  label?: string;
  selectedlabel?: string;
  disabled?: boolean;
  object?: any;
}

interface DropdownAttributes {
  options: DropdownOption[];
  onChange: (option: DropdownOption) => void;
  selectedValue?: any;
  className?: string;
  selectClassName?: string;
  size?: 'small' | 'large';
  placeholder?: string;
  disabled?: boolean;
  addShowAllOption?: boolean;
  fixedLabel?: boolean;
  label?: string;
  shouldCapitalizeFirstLetter?: boolean;
  activeEffect?: boolean;
  onBlur?: (value: DropdownOption) => void;
}

const Dropdown = ({
  options,
  selectedValue,
  onChange,
  className = '',
  selectClassName = '',
  size = 'large',
  placeholder,
  disabled = false,
  fixedLabel = false,
  label,
  shouldCapitalizeFirstLetter = false,
  activeEffect = false,
  onBlur,
}: DropdownAttributes) => {
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [newPlaceholder, setNewPlaceholder] = useState<any>(placeholder);
  const [filteredOptions, setFilteredOptions] = useState<any>(options);

  const dropdownRef = useRef<HTMLUListElement>(null);

  const selectedOption = useMemo(
    () => options?.find((o) => o.value == selectedValue),
    [selectedValue, options]
  );

  const placeholderVisible = useMemo(
    () => !!placeholder && !selectedOption,
    [placeholder, selectedOption]
  );

  const onOptionSelected = (value: string | undefined) => {
    const selectedOption = options?.find((o) => o.value == value);
    if (selectedOption) {
      setSearchTerm('');
      onChange(selectedOption);
    }
    setDropdownOpen(false);
  };

  useEffect(() => {
    if (!dropdownOpen) {
      setSearchTerm('');
    }
  }, [dropdownOpen]);

  // Update filteredOptions whenever options or searchTerm changes
  useEffect(() => {
    const newFilteredOptions = options.filter(
      (option) =>
        option.label &&
        option.label.toLowerCase().startsWith(searchTerm.toLowerCase())
    );

    if (newFilteredOptions.length > 0) {
      setFilteredOptions(newFilteredOptions);
    }
  }, [options, searchTerm]);

  const handleKeyDown = (key: string) => {
    if (/^[a-zA-ZåäöÅÄÖ ]$/.test(key)) {
      var tempSearchTerm = searchTerm + key.toLowerCase();

      const matchingOption = options.find(
        (option) =>
          option.label &&
          option.label.toLowerCase().startsWith(tempSearchTerm)
      );

      if (matchingOption && dropdownRef.current) {
        setSearchTerm(tempSearchTerm);
        setNewPlaceholder(tempSearchTerm);
        const matchingOptionIndex = options.indexOf(matchingOption);

        const matchingOptionNode =
          dropdownRef.current.childNodes[matchingOptionIndex];

        if (matchingOptionNode instanceof HTMLElement) {
          matchingOptionNode.focus();
        }
      }
    } else if (key === 'Backspace') {
      setSearchTerm(searchTerm.slice(0, -1));
    }
  };

  const selectClasses = useMemo(
    () =>
      clsx(
        selectClassName,
        'rounded flex w-full cursor-pointer appearance-none items-center border border-solid border-gray-300 text-lg focus:outline-none bg-white',
        size == 'large' ? 'py-3 px-4' : 'px-3 py-2',
        activeEffect && dropdownOpen && 'border-gray-700'
      ),
    [size]
  );

  const onDropdownBlur = (value: string | undefined) => {
    const selectedOption = options?.find((o) => o.value == value);
    if (selectedOption && onBlur) {
      onBlur(selectedOption);
    }
  };

  const capitalizeFirstLetter = (string) => {
    var lowerCasedString = string?.toLowerCase();
    var firstLetterUppercase = lowerCasedString?.charAt(0).toUpperCase();
    var stringWithoutFirstLetter = lowerCasedString?.slice(1);
    return firstLetterUppercase + stringWithoutFirstLetter;
  };

  const iconClasses = useMemo(
    () =>
      clsx(
        'h-4 w-4 transition-[transform] duration-300',
        dropdownOpen ? 'rotate-180' : 'rotate-0'
      ),
    [dropdownOpen]
  );

  return (
    <>
      <div className={clsx(className, 'relative lg:hidden')}>
        <select
          className={selectClasses}
          value={selectedValue ?? newPlaceholder}
          onChange={(e) => onOptionSelected(e.target.value)}
          onClick={() => setDropdownOpen(!dropdownOpen)}
          onBlur={(e) => {
            onBlur ? onDropdownBlur(e.target.value) : undefined;
            setDropdownOpen(false);
          }}
          disabled={disabled}
          onKeyDown={(e) => handleKeyDown(e.key)}
          required={true}
        >
          {newPlaceholder && (
            <option disabled value={newPlaceholder} className="text-md">
              {shouldCapitalizeFirstLetter
                ? capitalizeFirstLetter(newPlaceholder)
                : newPlaceholder}
            </option>
          )}
          {options.map((option, index) => (
            <option
              className="text-md"
              disabled={option.disabled}
              value={option.value}
              key={option.value + index + 'mobile'}
            >
              {shouldCapitalizeFirstLetter
                ? capitalizeFirstLetter(option.label ?? option.value)
                : option.label ?? option.value}
            </option>
          ))}
        </select>
        <Icon
          className={clsx(
            iconClasses,
            'absolute right-3 top-1/2 -translate-y-1/2'
          )}
          name="caret-down"
        />
        <label className="absolute -top-2 left-[14px] bg-white px-1 text-sm text-gray-700">
          {label}
        </label>
      </div>
      <div
        className={clsx(
          className,
          'relative hidden w-full text-gray-900 lg:block',
          disabled && 'pointer-events-none bg-gray-100'
        )}
        onBlur={() => setDropdownOpen(false)}
        onKeyDown={(e) => handleKeyDown(e.key)}
        tabIndex={1}
      >
        <div
          onClick={() => setDropdownOpen(!dropdownOpen)}
          onKeyDown={(e) => handleKeyDown(e.key)}
          className={selectClasses}
        >
          <div
            className={clsx(
              'flex-1',
              placeholderVisible && 'text-clip text-md text-gray-400'
            )}
          >
            {shouldCapitalizeFirstLetter
              ? capitalizeFirstLetter(
                  selectedOption?.selectedlabel
                    ? selectedOption.selectedlabel
                    : selectedOption?.label ?? selectedOption?.value
                )
              : selectedOption?.selectedlabel
              ? selectedOption?.selectedlabel
              : selectedOption?.label ??
                selectedOption?.value ??
                newPlaceholder}
          </div>
          <Icon className={iconClasses} name="caret-down" />
        </div>
        <label className="absolute -top-2 left-[14px] bg-white px-1 text-sm text-gray-700">
          {label}
        </label>
        {dropdownOpen && (
          <ul
            ref={dropdownRef}
            className="dropdown-option-list absolute top-full z-10 mt-1 max-h-80 w-full overflow-y-auto rounded border border-solid border-gray-300 bg-white shadow-[0px_13px_37px_0px_rgba(0,0,0,0.21)] scrollbar-none"
          >
            {filteredOptions.map((option, index) => {
              const optionText = shouldCapitalizeFirstLetter
                ? capitalizeFirstLetter(option.label ?? option.value)
                : option.label ?? option.value;

              const renderedText = optionText.replace(
                new RegExp(`(${searchTerm})`, 'ig'),
                (match, p1) => `<strong>${p1}</strong>`
              );

              return (
                <li
                  className={clsx(
                    'w-full cursor-pointer border-b border-solid border-gray-300 px-3 py-2.5 text-md hover:bg-gray-100 [&:last-child]:border-b-0',
                    option.value == selectedValue && 'font-bold',
                    (option.value == selectedValue || option.disabled) &&
                      'pointer-events-none'
                  )}
                  aria-selected={option.value == selectedValue}
                  onClick={() => {
                    onOptionSelected(option.value);
                    setDropdownOpen(false);
                  }}
                  key={option.value + index + 'desktop'}
                >
                  <div
                    dangerouslySetInnerHTML={{ __html: renderedText }}
                  />
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </>
  );
};

export default Dropdown;
